function pad(n) {
  return String(n).padStart(2, "0");
}

/** Vista compacta dd/mm hh:mm (mismo formato que citas). */
export function formatCitaPreview(dt) {
  if (!dt || String(dt).length < 10) return "—";
  const s = String(dt);
  const d = s.slice(0, 10);
  const t = s.includes("T") ? s.slice(11, 16) : "";
  return `${d.slice(8)}/${d.slice(5, 7)} ${t || "—"}`;
}

export function ndTodoElDia(val) {
  return !(val === false || val === "f" || val === "false");
}

export function ndFormFromApi(nd) {
  const fi = (nd.fecha_inicio || "").slice(0, 10);
  const ff = (nd.fecha_fin || "").slice(0, 10);
  const todoElDia = ndTodoElDia(nd.todo_el_dia);
  const hi = nd.hora_inicio ? String(nd.hora_inicio).slice(0, 5) : "09:00";
  const hf = nd.hora_fin ? String(nd.hora_fin).slice(0, 5) : "18:00";
  const hoy = fi || new Date().toISOString().slice(0, 10);
  return {
    ...nd,
    todo_el_dia: todoElDia,
    dt_inicio: `${fi || hoy}T${todoElDia ? "09:00" : hi}`,
    dt_fin: `${ff || fi || hoy}T${todoElDia ? "18:00" : hf}`,
  };
}

export function ndFormToApi(form) {
  const todoElDia = ndTodoElDia(form.todo_el_dia);
  const di = form.dt_inicio || form.fecha_inicio || "";
  const df = form.dt_fin || form.fecha_fin || "";
  const payload = {
    agente_id: form.agente_id,
    motivo: form.motivo,
    tipo: form.tipo,
    fecha_inicio: di.slice(0, 10),
    fecha_fin: df.slice(0, 10),
    hora_inicio: todoElDia ? null : (di.includes("T") ? di.slice(11, 16) : null),
    hora_fin: todoElDia ? null : (df.includes("T") ? df.slice(11, 16) : null),
    todo_el_dia: todoElDia,
  };
  if (form.id != null) payload.id = form.id;
  return payload;
}

export function formatNdPeriodo(nd) {
  const todoElDia = ndTodoElDia(nd.todo_el_dia);
  const fi = (nd.fecha_inicio || "").slice(0, 10);
  const ff = (nd.fecha_fin || "").slice(0, 10);
  if (todoElDia) {
    if (fi === ff) return `${formatCitaPreview(`${fi}T00:00`)} (todo el día)`;
    return `${formatCitaPreview(`${fi}T00:00`)} → ${formatCitaPreview(`${ff}T00:00`)} (todo el día)`;
  }
  const hi = String(nd.hora_inicio || "09:00").slice(0, 5);
  const hf = String(nd.hora_fin || "18:00").slice(0, 5);
  if (fi === ff) return `${formatCitaPreview(`${fi}T${hi}`)} → ${hf}`;
  return `${formatCitaPreview(`${fi}T${hi}`)} → ${formatCitaPreview(`${ff}T${hf}`)}`;
}

export function fechaISO(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Un agente puede tener vacaciones y guardia_sabado el mismo día; en cabecera/cuerpo mostramos uno solo. */
const ND_TIPO_PRIORIDAD = {
  vacaciones: 0,
  baja: 1,
  guardia_sabado: 2,
  horario_base: 3,
  otro: 4,
};

/** Apertura/cierre de la rejilla de agenda (no pintar fuera de horario oficina). */
export const GRID_OFICINA_APERTURA = "09:30";
export const GRID_OFICINA_CIERRE = "19:00";
/** Primera hora etiquetada en la columna semana (09:00). */
export const GRID_AGENDA_HORA_INICIO = 9;

function timeToMin(hhmm) {
  const p = String(hhmm || "00:00").slice(0, 5).split(":");
  return Number(p[0]) * 60 + Number(p[1] || 0);
}

function minToTime(m) {
  return `${pad(Math.floor(m / 60))}:${pad(m % 60)}`;
}

/** Recorta cualquier bloque parcial a 09:30–19:00. Devuelve null si no queda franja visible. */
export function clipNdParcialParaGrid(nd) {
  if (!nd || ndTodoElDia(nd.todo_el_dia)) return nd;
  const gridIni = timeToMin(GRID_OFICINA_APERTURA);
  const gridFin = timeToMin(GRID_OFICINA_CIERRE);
  const ini = timeToMin(nd.hora_inicio);
  const fin = timeToMin(nd.hora_fin);
  const clipIni = Math.max(ini, gridIni);
  const clipFin = Math.min(fin, gridFin);
  if (clipFin - clipIni < 15) return null;
  return {
    ...nd,
    hora_inicio: minToTime(clipIni),
    hora_fin: minToTime(clipFin),
  };
}

/** @deprecated Usar clipNdParcialParaGrid */
export function clipNdHorarioBaseParaGrid(nd) {
  return clipNdParcialParaGrid(nd);
}

/** Posición en px de una franja dentro de la rejilla semana. */
export function ndFranjaSemanaPx(horaInicio, horaFin, altoHora = 60, horaGridInicio = GRID_AGENDA_HORA_INICIO) {
  const iniMin = timeToMin(horaInicio);
  const finMin = timeToMin(horaFin);
  const gridBase = horaGridInicio * 60;
  const topPx = ((iniMin - gridBase) / 60) * altoHora;
  const altoPx = Math.max(((finMin - iniMin) / 60) * altoHora, 20);
  return { topPx, altoPx };
}

/** Franja visible para bloques «todo el día» (09:30–19:00, sin invadir fila 19h). */
export function ndFranjaTodoElDiaSemanaPx(altoHora = 60, horaGridInicio = GRID_AGENDA_HORA_INICIO) {
  return ndFranjaSemanaPx(GRID_OFICINA_APERTURA, GRID_OFICINA_CIERRE, altoHora, horaGridInicio);
}

function sortNdPorAgente(a, b) {
  const na = String(a.agente_nombre || "").localeCompare(String(b.agente_nombre || ""));
  if (na !== 0) return na;
  return String(a.id).localeCompare(String(b.id));
}

/**
 * Índice de columna por agente (misma ordenación en todo el día).
 * Une bloques todo el día y parciales para alinear franjas verticales como los sábados.
 */
export function buildAgentesColumnasIndex(ndTodoList, ndParcialList) {
  const byAgent = new Map();
  for (const nd of [...(ndTodoList || []), ...(ndParcialList || [])]) {
    const key = String(nd.agente_id || nd.id);
    if (!byAgent.has(key)) byAgent.set(key, nd);
  }
  const order = [...byAgent.values()].sort(sortNdPorAgente).map((nd) => String(nd.agente_id || nd.id));
  const colMap = new Map(order.map((id, i) => [id, i]));
  return { count: order.length, colMap, order };
}

export function ndParcialesParaColumnas(list) {
  return (list || [])
    .map(clipNdParcialParaGrid)
    .filter(Boolean)
    .sort((a, b) => {
      const ta = timeToMin(a.hora_inicio) - timeToMin(b.hora_inicio);
      if (ta !== 0) return ta;
      return sortNdPorAgente(a, b);
    });
}

export function formatNdMotivoCorto(nd) {
  if (!nd) return "";
  const motivo = String(nd.motivo || "").trim();
  if (motivo.includes(" — ")) {
    return motivo.split(" — ").pop().trim();
  }
  const tipo = nd.tipo || "";
  if (tipo === "vacaciones") return motivo || "Vacaciones";
  if (tipo === "baja") return motivo || "Baja";
  if (tipo === "guardia_sabado") return "No trabaja";
  if (tipo === "horario_base") return motivo || "No trabaja";
  if (tipo === "festivo") return motivo || "Festivo";
  return motivo || "No disponible";
}

export function dedupeNdPorAgente(list) {
  if (!Array.isArray(list) || list.length === 0) return [];
  const byAgent = new Map();
  for (const nd of list) {
    const key = String(nd.agente_id || nd.id);
    const prev = byAgent.get(key);
    if (!prev) {
      byAgent.set(key, nd);
      continue;
    }
    const pPrev = ND_TIPO_PRIORIDAD[prev.tipo] ?? 9;
    const pNew = ND_TIPO_PRIORIDAD[nd.tipo] ?? 9;
    if (pNew < pPrev) byAgent.set(key, nd);
  }
  return [...byAgent.values()];
}

export { pad };
